'use client'

import { BodyText } from '@/components/ui/typography'
import type { ReactNode } from 'react'
import { useTheme } from '@/components/theme-provider'
import { useSettings } from '@/hooks/useSettings'
import { useOffline } from '@/components/offline-context'
import { OfflineStatus } from '@/components/status/offline-status'
import { useAuth } from '@/hooks/useAuth'
import { LoginDialog } from '@/components/auth/login-dialog'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/routing'
import { canonicalRoutes } from '@/lib/product-routes'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  IconMoon,
  IconSun,
  IconDeviceDesktop,
  IconChevronDown,
  IconLogout,
  IconCheck,
  IconSparkles,
  IconCrown,
  IconCreditCard,
  IconDatabase,
  IconRefresh,
  IconShieldCheck
} from '@tabler/icons-react'
import { useState } from 'react'
import { useEntitlements } from '@/hooks/useEntitlements'
import { UpgradeIntentDialog } from '@/components/paywall/upgrade-intent-dialog'
import { analytics } from '@/lib/analytics'
import { createCustomerPortalSession } from '@/lib/billing'
import type { PaywallType } from '@/lib/monetization'

// Content-only component - PageLayout is rendered by the server page
export function AppSettingsContent() {
  const t = useTranslations('settings')
  const tCommon = useTranslations('common')
  const { theme, setTheme } = useTheme()
  const { user, signOut, isLoading: isAuthLoading } = useAuth()
  const {
    settings,
    setScriptDisplay,
    setScriptPriority,
    setPronunciationDisplay,
    setPronunciationPriority,
    setChineseFont,
    setDefinitionLocale,
    setFlashcardAutoplayAudio
  } = useSettings()
  const {
    isOfflineEnabled,
    isReady,
    hasOfflineData,
    progress,
    statusText,
    toggleOffline,
    deleteDictionary,
    redownloadDictionary,
    localFile,
    currentVersion,
    latestVersion,
    isUpdateAvailable,
    autoUpdateEnabled,
    setAutoUpdateEnabled,
    isOfflineSupported,
    offlineSupportMessage,
    offlineError,
    manifestTiers,
    currentTier
  } = useOffline()
  const {
    isPremium,
    paywallsEnabled,
    dodoBillingEnabled,
    billingStatus,
    entitlementSource,
    refreshEntitlements,
    pricing
  } = useEntitlements()
  const [upgradeDialogConfig, setUpgradeDialogConfig] = useState<{
    open: boolean
    paywallType: PaywallType
    surface: string
    title?: string
    description?: string
  }>({
    open: false,
    paywallType: 'premium-chapters',
    surface: 'app-settings'
  })
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  const planName = paywallsEnabled
    ? isPremium
      ? 'Pro'
      : 'Free'
    : 'Free access'
  const billingStatusLabel = formatBillingStatus(billingStatus)
  const yearlySavings = Math.max(
    0,
    pricing.weeklyUsd * 52 - pricing.yearlyUsd
  ).toFixed(2)
  const canManageSubscription =
    dodoBillingEnabled &&
    billingStatus !== 'none' &&
    billingStatus !== 'unknown'
  const planSummary = !paywallsEnabled
    ? 'All stories and learning tools are available while subscriptions are paused.'
    : isPremium
      ? 'Your Pro features are active on this account.'
      : 'The dictionary, offline saves, and document reader are free. Pro unlocks selected stories and chapters.'

  const openUpgradeDialog = (config: {
    paywallType: PaywallType
    surface: string
    title?: string
    description?: string
  }) => {
    setUpgradeDialogConfig({
      open: true,
      ...config
    })
  }

  const handleOfflineToggle = () => {
    toggleOffline()
  }

  const handleOpenPortal = async () => {
    setIsOpeningPortal(true)
    try {
      const result = await createCustomerPortalSession()
      analytics.portalOpened('app-settings', {
        entitlement_source: entitlementSource,
        billing_status: billingStatus
      })
      window.location.href = result.link
    } catch (error) {
      console.error('Failed to open customer portal', error)
    } finally {
      setIsOpeningPortal(false)
    }
  }

  return (
    <div className="mx-auto box-border flex w-full max-w-4xl flex-col gap-4 overflow-y-auto px-4 py-5 md:px-6 md:py-6">
      {/* Account */}
      <SettingSection label={t('account')}>
        {isAuthLoading ? (
          <div className="space-y-2">
            <div className="bg-muted/60 h-4 w-40 animate-pulse rounded" />
            <div className="bg-muted/60 h-3 w-32 animate-pulse rounded" />
          </div>
        ) : user ? (
          <div className="space-y-4">
            <div className="bg-background overflow-hidden rounded-2xl border">
              <div className="border-border/70 flex flex-col gap-4 border-b p-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <p className="text-foreground font-semibold">
                      {(user.user_metadata?.full_name as string | undefined) ??
                        user.email ??
                        'Account'}
                    </p>
                    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium">
                      {paywallsEnabled && isPremium ? (
                        <IconCrown className="h-3.5 w-3.5 text-amber-500" />
                      ) : (
                        <IconSparkles className="text-primary h-3.5 w-3.5" />
                      )}
                      {planName}
                    </span>
                  </div>
                  {user.email && user.user_metadata?.full_name && (
                    <BodyText className="text-muted-foreground text-sm">
                      {user.email}
                    </BodyText>
                  )}
                  <BodyText className="text-muted-foreground mt-2 max-w-2xl text-sm">
                    {planSummary}
                  </BodyText>
                </div>

                {paywallsEnabled && !isPremium ? (
                  <Button
                    size="sm"
                    className="shrink-0 rounded-full"
                    onClick={() =>
                      openUpgradeDialog({
                        paywallType: 'premium-chapters',
                        surface: 'settings-account',
                        title: 'Unlock Pro stories'
                      })
                    }
                  >
                    Upgrade to Pro
                  </Button>
                ) : paywallsEnabled ? (
                  <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold">
                    <IconShieldCheck className="h-4 w-4" />
                    Active
                  </div>
                ) : null}
              </div>

              <div
                className={
                  paywallsEnabled
                    ? 'grid gap-0 md:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]'
                    : ''
                }
              >
                <div className="space-y-3 p-4">
                  <p className="text-sm font-semibold">
                    What your plan includes
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <PlanFeature
                      active
                      icon={<IconDatabase className="h-4 w-4" />}
                      label="Complete offline dictionary"
                      description="Download the full database for offline lookup."
                    />
                    <PlanFeature
                      active
                      icon={<IconCheck className="h-4 w-4" />}
                      label="Unlimited offline story saves"
                      description="Keep stories available without a connection."
                    />
                    <PlanFeature
                      active
                      icon={<IconCheck className="h-4 w-4" />}
                      label="Document reader"
                      description="Import your own texts for study."
                    />
                    <PlanFeature
                      active={!paywallsEnabled || isPremium}
                      icon={<IconSparkles className="h-4 w-4" />}
                      label={
                        paywallsEnabled
                          ? 'Pro stories and chapters'
                          : 'All stories and chapters'
                      }
                      description="Read selected graded stories in full."
                    />
                  </div>
                </div>

                {paywallsEnabled && (
                  <div className="border-border/70 border-t p-4 md:border-t-0 md:border-l">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <IconCreditCard className="text-muted-foreground h-4 w-4" />
                        <p className="text-sm font-semibold">Billing</p>
                      </div>
                      <dl className="space-y-2 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">Status</dt>
                          <dd className="font-medium">{billingStatusLabel}</dd>
                        </div>
                        {!isPremium && (
                          <div className="flex items-center justify-between gap-3">
                            <dt className="text-muted-foreground">
                              Yearly value
                            </dt>
                            <dd className="font-medium">
                              Save ${yearlySavings}/year
                            </dd>
                          </div>
                        )}
                      </dl>

                      {canManageSubscription && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full justify-center rounded-full"
                          onClick={() => void handleOpenPortal()}
                          disabled={isOpeningPortal}
                        >
                          {isOpeningPortal
                            ? 'Opening portal…'
                            : 'Manage subscription'}
                        </Button>
                      )}

                      {canManageSubscription && (
                        <BodyText className="text-muted-foreground text-xs">
                          Manage cancellation, plan changes, payment methods,
                          and invoices in the secure billing portal.
                        </BodyText>
                      )}

                      {user && dodoBillingEnabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="w-full justify-center gap-2 rounded-full"
                          onClick={() => void refreshEntitlements()}
                        >
                          <IconRefresh className="h-4 w-4" />
                          Refresh billing status
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="gap-2 self-start"
              onClick={() => void signOut()}
            >
              <IconLogout className="h-4 w-4" />
              {t('signOut')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <BodyText className="text-muted-foreground text-sm">
              {t('notSignedIn')} Complete offline dictionary downloads are free
              on supported browsers.
            </BodyText>
            <LoginDialog>
              <Button size="sm" className="self-start sm:self-auto">
                {tCommon('signIn')}
              </Button>
            </LoginDialog>
          </div>
        )}
      </SettingSection>

      {/* Appearance */}
      <SettingSection label={t('appearance')}>
        <div className="space-y-3">
          <BodyText className="text-muted-foreground text-xs">
            {t('appearanceDescription')}
          </BodyText>
          <ButtonGroup
            current={theme}
            onChange={setTheme}
            options={[
              {
                value: 'light',
                label: t('light'),
                icon: <IconSun className="h-4 w-4" />
              },
              {
                value: 'dark',
                label: t('dark'),
                icon: <IconMoon className="h-4 w-4" />
              },
              {
                value: 'system',
                label: t('system'),
                icon: <IconDeviceDesktop className="h-4 w-4" />
              }
            ]}
          />
        </div>
      </SettingSection>

      {/* Chinese display */}
      <SettingSection label={t('characterDisplay')}>
        <div className="space-y-4">
          <BodyText className="text-muted-foreground text-xs">
            {t('characterDescription')}
          </BodyText>
          <SettingRow
            label={t('displayMode')}
            controls={
              <ButtonGroup
                current={settings.scriptDisplay}
                onChange={setScriptDisplay}
                options={[
                  { value: 'both', label: t('both') },
                  { value: 'simplified', label: t('simplifiedOnly') },
                  { value: 'traditional', label: t('traditionalOnly') }
                ]}
              />
            }
          />
          {settings.scriptDisplay === 'both' && (
            <SettingRow
              label={t('priority')}
              controls={
                <ButtonGroup
                  current={settings.scriptPriority}
                  onChange={setScriptPriority}
                  options={[
                    { value: 'simplified', label: t('simplifiedFirst') },
                    { value: 'traditional', label: t('traditionalFirst') }
                  ]}
                />
              }
            />
          )}
          <Separator />
          <SettingRow
            label={t('pronunciationDisplay')}
            controls={
              <ButtonGroup
                current={settings.pronunciationDisplay}
                onChange={setPronunciationDisplay}
                options={[
                  { value: 'both', label: t('both') },
                  { value: 'pinyin', label: t('pinyinOnly') },
                  { value: 'zhuyin', label: t('zhuyinOnly') }
                ]}
              />
            }
          />
          {settings.pronunciationDisplay === 'both' && (
            <SettingRow
              label={t('priority')}
              controls={
                <ButtonGroup
                  current={settings.pronunciationPriority}
                  onChange={setPronunciationPriority}
                  options={[
                    { value: 'pinyin', label: t('pinyinFirst') },
                    { value: 'zhuyin', label: t('zhuyinFirst') }
                  ]}
                />
              }
            />
          )}
          <Separator />
          <SettingRow
            label={t('chineseFont')}
            controls={
              <DropdownSelect
                value={settings.chineseFont}
                onChange={setChineseFont}
                options={[
                  { value: 'system', label: t('systemDefault') },
                  { value: 'lxgw-wenkai-tc', label: t('lxgwWenkaiTc') },
                  { value: 'noto-sans-tc', label: t('notoSansTc') },
                  { value: 'noto-sans-sc', label: t('notoSansSc') }
                ]}
              />
            }
          />
        </div>
      </SettingSection>

      {/* Study preferences */}
      <SettingSection label={`${t('definitionLanguage')} · ${t('flashcards')}`}>
        <div className="space-y-4">
          <BodyText className="text-muted-foreground text-xs">
            {t('definitionLanguageDescription')}
          </BodyText>
          <SettingRow
            label={t('definitionLanguage')}
            controls={
              <DropdownSelect
                value={settings.definitionLocale}
                onChange={setDefinitionLocale}
                options={[
                  { value: 'auto', label: t('definitionLocaleAuto') },
                  { value: 'en', label: t('definitionLocaleEnglish') }
                ]}
              />
            }
          />
          <Separator />
          <SettingRow
            label={t('autoplayAudio')}
            controls={
              <Switch
                id="autoplay-toggle"
                checked={settings.flashcardAutoplayAudio}
                onCheckedChange={setFlashcardAutoplayAudio}
              />
            }
          />
        </div>
      </SettingSection>

      {/* Learning path */}
      <SettingSection label={t('learningPath')}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <BodyText className="text-muted-foreground max-w-xl text-sm">
            {t('learningPathDescription')}
          </BodyText>
          <Button size="sm" className="gap-2 self-start rounded-full" asChild>
            <Link href={canonicalRoutes.onboarding}>
              <IconSparkles className="h-4 w-4" />
              {t('restartOnboarding')}
            </Link>
          </Button>
        </div>
      </SettingSection>

      {/* Offline Data */}
      <SettingSection label={t('offlineData')}>
        <OfflineStatus
          isOfflineMode={isOfflineEnabled}
          hasOfflineData={hasOfflineData}
          downloadProgress={progress}
          statusText={statusText}
          isReady={isReady}
          onToggle={handleOfflineToggle}
          onDelete={deleteDictionary}
          onRedownload={redownloadDictionary}
          currentVersion={currentVersion}
          latestVersion={latestVersion}
          isUpdateAvailable={isUpdateAvailable}
          localFile={localFile}
          autoUpdateEnabled={autoUpdateEnabled}
          onToggleAutoUpdate={setAutoUpdateEnabled}
          isSupported={isOfflineSupported}
          supportMessage={offlineSupportMessage}
          offlineError={offlineError}
          manifestTiers={manifestTiers}
          currentTier={currentTier}
          isAuthenticated={!!user}
        />
      </SettingSection>

      {paywallsEnabled && (
        <UpgradeIntentDialog
          open={upgradeDialogConfig.open}
          onOpenChange={(open) =>
            setUpgradeDialogConfig((prev) => ({ ...prev, open }))
          }
          paywallType={upgradeDialogConfig.paywallType}
          surface={upgradeDialogConfig.surface}
          title={upgradeDialogConfig.title}
          description={upgradeDialogConfig.description}
        />
      )}
    </div>
  )
}

// Helper Components
interface SettingSectionProps {
  label: string
  children: ReactNode
}

function SettingSection({ label, children }: SettingSectionProps) {
  return (
    <Card className="gap-4 py-4">
      <CardHeader className="px-6 py-0">
        <CardTitle className="text-base">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">{children}</CardContent>
    </Card>
  )
}

interface PlanFeatureProps {
  active: boolean
  icon: ReactNode
  label: string
  description: string
}

function PlanFeature({ active, icon, label, description }: PlanFeatureProps) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        active
          ? 'border-border/70 bg-background'
          : 'border-border/50 bg-muted/25 text-muted-foreground'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
            active
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {icon}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium">{label}</span>
          <span className="text-muted-foreground mt-1 block text-xs leading-5">
            {description}
          </span>
        </span>
      </div>
    </div>
  )
}

function formatBillingStatus(status: string) {
  switch (status) {
    case 'active':
      return 'Active'
    case 'on_hold':
      return 'Payment action needed'
    case 'cancelled':
      return 'Cancelled'
    case 'failed':
      return 'Payment failed'
    case 'expired':
      return 'Expired'
    case 'pending':
      return 'Pending'
    case 'unknown':
      return 'Unknown'
    default:
      return 'No subscription'
  }
}

interface SettingRowProps {
  label: string
  controls: ReactNode
}

function SettingRow({ label, controls }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <Label className="text-muted-foreground text-sm">{label}</Label>
      {controls}
    </div>
  )
}

interface ButtonGroupProps<T extends string> {
  options: Array<{ value: T; label: string; icon?: ReactNode }>
  current: T
  onChange: (value: T) => void
}

function ButtonGroup<T extends string>({
  options,
  current,
  onChange
}: ButtonGroupProps<T>) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(({ value, label, icon }) => (
        <Button
          key={value}
          variant={current === value ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(value)}
          className={icon ? 'gap-2' : ''}
        >
          {icon}
          {label}
        </Button>
      ))}
    </div>
  )
}

interface DropdownSelectProps<T extends string> {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}

function DropdownSelect<T extends string>({
  value,
  options,
  onChange
}: DropdownSelectProps<T>) {
  const currentLabel =
    options.find((opt) => opt.value === value)?.label || value
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {currentLabel}
          <IconChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((opt) => (
          <DropdownMenuItem key={opt.value} onClick={() => onChange(opt.value)}>
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Backwards compatibility export
export { AppSettingsContent as AppSettingsClient }
